import { SelectItem } from "@mantine/core";
import { GraphQL, useRawGraphqlRequest } from "@rpi/openapi-api";
import { RpaDropdown } from "../inputFields/RpaDropdownInput";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { EqualsSearchInput, SearchInput } from "../../configuration/filters/searchInput";
export const NO_FILTER_VALUE = "All";

export function getInitialOrganization<TField extends string>(field: TField, filters: SearchInput<TField>[]) {
  return filters?.find((value) => value?.field === field)?.firstValue ?? NO_FILTER_VALUE;
}

export function getCustomerPublicIdFilter<TField extends "customerPublicId">(
  field: TField,
  customerPublicId: string
): EqualsSearchInput<TField> | null {
  return customerPublicId !== NO_FILTER_VALUE
    ? {
        field: field,
        condition: GraphQL.SearchCondition.Equals,
        firstValue: customerPublicId,
      }
    : null;
}

type OrganizationFilterProps = {
  value: string;
  onChange: (newValue: string) => void;
};

export function OrganizationFilter({ value, onChange }: OrganizationFilterProps) {
  const organizations = useAllCustomers();
  const [orgSearch, setOrgSearch] = useState<string>("");

  return (
    <RpaDropdown
      data-testid="organizations-dropdown"
      label="Organizations"
      data={toSelectionData(organizations)}
      searchable
      searchValue={orgSearch}
      onSearchChange={setOrgSearch}
      onDropdownOpen={() => setOrgSearch("")}
      value={value}
      onChange={onChange}
      disabled={organizations === undefined}
    />
  );
}

function toSelectionData(
  organizations?: GraphQL.AdminSearchCustomersForFilterQuery["adminSearchCustomers"]["data"]
): SelectItem[] {
  const customersAsSelectData =
    organizations?.map<SelectItem>((customer) => ({
      label: customer.organizationName ? `${customer.organizationName} (${customer.publicId})` : customer.publicId,
      value: customer.publicId,
    })) ?? [];
  const all: SelectItem = { label: "All", value: NO_FILTER_VALUE };
  return [all, ...customersAsSelectData];
}

/**
 * query to paginate through all results for the adminSearchCustomers query
 * this allows us to get all customers for frontend 'search' without having to query the backend on user input
 * or allow an unbounded query in the backend
 * @returns A list of all customers
 */
export function useAllCustomers() {
  const graphqlRequest = useRawGraphqlRequest();
  const customersQuery = useQuery({
    queryKey: ["admin-list-all-customers"],
    queryFn: async () => {
      const allCustomers: GraphQL.AdminSearchCustomersForFilterQuery["adminSearchCustomers"]["data"] = [];
      let remainingCustomers = 0;
      let nextOffset = 0;
      do {
        const { adminSearchCustomers } = await graphqlRequest<
          GraphQL.AdminSearchCustomersForFilterQuery,
          GraphQL.AdminSearchCustomersForFilterQueryVariables
        >(GraphQL.AdminSearchCustomersForFilterDocument, {
          limit: 1000,
          offset: nextOffset,
        });

        if (
          !adminSearchCustomers.nextOffset ||
          !adminSearchCustomers.totalCount ||
          adminSearchCustomers.data.length === 0
        ) {
          remainingCustomers = 0;
        } else {
          nextOffset = adminSearchCustomers.nextOffset;
          allCustomers.push(...adminSearchCustomers.data);
          remainingCustomers = adminSearchCustomers.totalCount - allCustomers.length;
        }
      } while (remainingCustomers > 0);

      return allCustomers;
    },
  });
  return customersQuery.data;
}
